import React from "react";
import { useNavigate } from "react-router-dom";

export interface OrderItem {
  id: number | string;
  quantity: number;
  price: number;
  product: {
    id: number | string;
    name: string;
  };
}

interface ProductContextItem {
  product: {
    id: number | string;
    images?: { image: string }[];
  };
}

interface OrderItemsListProps {
  items: OrderItem[];
  products: ProductContextItem[];
  isExpanded: boolean;
  onToggle: () => void;
  isRTL?: boolean;
  t: (key: string, options?: any) => string;
  formatCurrency: (value: number) => string;
}

const OrderItemsList: React.FC<OrderItemsListProps> = ({
  items,
  products,
  isExpanded,
  onToggle,
  isRTL = false,
  t,
  formatCurrency,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mb-6">
      <div
        className={`flex items-center justify-between mb-4 ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <span className="text-purple-500">🛒</span>
          {t("order.orderItems")} ({items.length})
        </h3>
        <button
          onClick={onToggle}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          aria-expanded={isExpanded}
        >
          {isExpanded ? t("common.collapse") : t("common.expand")}
          <svg
            className={`w-4 h-4 transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-3">
          {items.map((item) => {
            const product = products.find(
              (p) => p.product.id === item.product.id
            );
            const image =
              product?.product.images?.[0]?.image || "/placeholder-image.jpg";

            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl hover:shadow-md transition-shadow ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <div className="relative">
                  <img
                    src={image}
                    alt={item.product.name}
                    className="w-20 h-20 object-cover rounded-lg"
                    onError={(e) =>
                      (e.currentTarget.src = "/placeholder-image.jpg")
                    }
                  />
                  <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {item.quantity}
                  </span>
                </div>

                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 mb-1">
                    {item.product.name}
                  </h4>
                  <div
                    className={`flex items-center gap-4 text-sm text-gray-600 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span>
                      {t("order.price")}: {formatCurrency(item.price)}
                    </span>
                    <span className="font-semibold text-green-600">
                      {t("order.subtotal")}: {" "}
                      {formatCurrency(item.price * item.quantity)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/product/${item.product.id}`)}
                  className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  👁️ {t("common.view")}
                </button>
              </div>
            );
          })}

          {/* Total */}
          <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
            <div
              className={`flex justify-between items-center ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span className="text-lg font-semibold">
                {t("order.orderTotal")}
              </span>
              <span className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  items.reduce(
                    (sum, item) => sum + item.price * item.quantity,
                    0
                  )
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItemsList;